import React, { useEffect, useState, useRef } from 'react';
import { getBookingHubById, getBookingHubLocationsById } from '../../api';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { setHubDetailsCache, setLocationsCache } from '../../redux/bookingHub';
import Icon, { SearchOutlined, FilterOutlined, GlobalOutlined, EnvironmentOutlined, CheckCircleOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import '../../assets/css/bookingHub.css';
import LocationCard from './components/LocationCard';

const BookingHub = ({ match }) => {
  const { gid } = match.params;
  const history = useHistory();
  const dispatch = useDispatch();

  const { hubDetailsByGid, locationsByGid } = useSelector(state => state.bookingHub);

  const [hubDetails, setHubDetails] = useState(null);
  const [locations, setLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTags, setActiveTags] = useState([]);
  const [selectedQuickBooking, setSelectedQuickBooking] = useState(0);

  const tagsContainerRef = useRef(null);

  const scrollTags = (direction) => {
    if (tagsContainerRef.current) {
      const scrollAmount = 150;
      tagsContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      // Check and load details
      if (hubDetailsByGid[gid]) {
        setHubDetails(hubDetailsByGid[gid]);
      } else {
        try {
          const details = await getBookingHubById(gid);
          setHubDetails(details);
          dispatch(setHubDetailsCache({ gid, details }));
        } catch (error) {
          console.error("Error fetching booking hub details", error);
        }
      }

      // Check and load locations
      if (locationsByGid[gid]) {
        setLocations(locationsByGid[gid]);
        setFilteredLocations(locationsByGid[gid]);
      } else {
        try {
          const locsEntity = await getBookingHubLocationsById(gid);
          const locsList = locsEntity?.bookingHubLocations || locsEntity?.content || [];
          setLocations(locsList);
          setFilteredLocations(locsList);
          dispatch(setLocationsCache({ gid, locations: locsList }));
        } catch (error) {
          console.error("Error fetching booking hub locations", error);
          setLocations([]);
          setFilteredLocations([]);
        }
      }

      setLoading(false);
    };
    fetchData();
  }, [gid, hubDetailsByGid, locationsByGid, dispatch]);

  // Prepend "All" to the API tags for the filter bar
  const allTags = hubDetails?.tags
    ? [{ gid: '__all__', name: 'All' }, ...hubDetails.tags]
    : [];

  useEffect(() => {
    if (hubDetails?.sliders?.length > 1) {
      const interval = setInterval(() => {
        setCurrentSlide(prev => (prev + 1) % hubDetails.sliders.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [hubDetails]);

  // Filter by search query AND active tags
  useEffect(() => {
    let filtered = locations;

    if (activeTags.length > 0) {
      filtered = filtered.filter(loc => {
        const locTags = loc.tags || [];
        // Match location if it has ANY of the selected activeTags
        return locTags.some(t => {
          const name = typeof t === 'string' ? t : t?.name;
          if (!name) return false;
          return activeTags.some(at => at.toLowerCase() === name.toLowerCase());
        });
      });
    }

    if (searchQuery) {
      const lowerQuery = searchQuery.toLowerCase();
      filtered = filtered.filter(loc =>
        (loc.name || '').toLowerCase().includes(lowerQuery) ||
        (loc.description || '').toLowerCase().includes(lowerQuery) ||
        (loc.landmark || '').toLowerCase().includes(lowerQuery)
      );
    }

    setFilteredLocations(filtered);
  }, [searchQuery, activeTags, locations]);

  if (loading) return <div>Loading...</div>;
  if (!hubDetails) return <div>Hub not found</div>;

  return (
    <div className="booking-hub-container">
      <header className="booking-hub-header">
        <div className="booking-hub-header-left">
          {hubDetails.logo && <img className="booking-hub-logo" src={hubDetails.logo} alt="Logo" />}
        </div>
        <div className="booking-hub-header-center">
          <div className="booking-hub-header-title" style={{ fontSize: '13px', fontWeight: '800', letterSpacing: '1px', marginBottom: '0px' }}>
            {hubDetails.header || 'HERITAGE SITE BOOKING PORTAL'}
          </div>
          <p className="booking-hub-header-subtitle" style={{ fontSize: '10px', color: '#888' }}>
            {hubDetails.subHeader || 'Government of Tamil Nadu - Department of Archaeology'}
          </p>
        </div>
      </header>

      <div className="booking-hub-hero-section">
        {hubDetails.sliders && hubDetails.sliders.length > 0 && (
          <div className="booking-hub-carousel">
            {hubDetails.sliders.map((slide, index) => (
              <div
                key={index}
                className={`booking-hub-slide ${index === currentSlide ? 'active' : ''}`}
                style={{ backgroundImage: `url(${slide.imageUrl})` }}
              >
                <div className="booking-hub-overlay" />
                  <div className="booking-hub-slide-content">
                    {slide.label && (
                      <span className="booking-hub-slide-label" style={{ color: hubDetails.colorCode || '#C9822A' }}>
                        <span className="dot" style={{ backgroundColor: hubDetails.colorCode || '#C9822A' }}></span> {slide.label}
                      </span>
                    )}
                    <div className="booking-hub-slide-title">
                      {slide.primaryText} <br />
                      <span style={{ color: hubDetails.colorCode || '#C9822A' }}>{slide.primaryThemedText}</span>
                    </div>
                    <p className="booking-hub-slide-desc">{slide.secondaryText}</p>

                    <div className="booking-hub-slide-indicators">
                      {hubDetails.sliders.map((_, i) => (
                        <div
                          key={i}
                          className={`booking-hub-slide-dot ${i === currentSlide ? 'active' : ''}`}
                          style={{
                            backgroundColor: i === currentSlide ? (hubDetails.colorCode || '#C9822A') : '#d1bba4'
                          }}
                          onClick={() => setCurrentSlide(i)}
                        ></div>
                      ))}
                    </div>
                  </div>
              </div>
            ))}
          </div>
        )}

        {/* FLOATING QUICK BOOKING OVERLAY */}
        {locations.length > 0  && hubDetails?.enableQuickBooking && (
          <div className="booking-hub-quick-booking-card">
            <div className="booking-hub-quick-booking-header">
              <div className="booking-hub-quick-booking-title">QUICK BOOKING</div>
              <span className="booking-hub-status-available"><span className="dot"></span> Available</span>
            </div>
            <div className="booking-hub-quick-booking-label">SELECT SITE</div>
            <div className="booking-hub-quick-booking-list">
              {locations.slice(0, 80).map((loc, i) => (
                <div
                  key={i}
                  className={`booking-hub-qb-item ${i === selectedQuickBooking ? 'selected' : ''}`}
                  style={i === selectedQuickBooking ? { borderColor: hubDetails.colorCode || '#C9822A', backgroundColor: '#fcf6ef' } : {}}
                  onClick={() => setSelectedQuickBooking(i)}
                >
                  <div className="booking-hub-qb-item-indicator" style={i === selectedQuickBooking ? { backgroundColor: hubDetails.colorCode || '#C9822A' } : {}}></div>
                  <div className="booking-hub-qb-item-text">
                    <div className="booking-hub-qb-item-name">{loc.landmark || loc.name}</div>
                    <div className="booking-hub-qb-item-landmark" style={i === selectedQuickBooking ? { color: hubDetails.colorCode || '#C9822A' } : {}}>{loc.name}</div>
                  </div>
                  {i === selectedQuickBooking && (
                    <CheckCircleOutlined className="booking-hub-qb-item-check" style={{ color: hubDetails.colorCode || '#C9822A' }} />
                  )}
                </div>
              ))}
            </div>
            <button
              className="booking-hub-qb-book-btn"
              style={{ backgroundColor: hubDetails.colorCode || '#C9822A' }}
              onClick={() => {
                const selectedLoc = locations[selectedQuickBooking];
                if (selectedLoc) {
                  history.push(`/booking-hub/${gid}/location/${selectedLoc.gid}/next`);
                }
              }}
            >
              BOOK NOW
            </button>
          </div>
        )}
      </div>

      <div className="booking-hub-filter-bar">
        <div className="booking-hub-filter-left">
          <div className="booking-hub-search-container">
            <SearchOutlined className="booking-hub-search-icon" />
            <input
              className="booking-hub-search-input"
              placeholder="Search sites..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {allTags.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', width: '100%', overflow: 'hidden' }}>
              <div className="booking-hub-filter-icon" style={{ flexShrink: 0, marginRight: '4px' }}><FilterOutlined /></div>
              
              <button 
                onClick={() => scrollTags('left')}
                className="booking-hub-tags-scroll-btn booking-hub-tags-scroll-btn-left"
              >
                <LeftOutlined style={{ fontSize: '10px' }} />
              </button>

              <div 
                ref={tagsContainerRef}
                className="booking-hub-tags-container" 
                style={{ display: 'flex', alignItems: 'center', gap: '8px', flexGrow: 1 }}
              >
                {allTags.map((tag, index) => {
                  const isAllTag = tag.name === 'All';
                  const isActive = isAllTag ? activeTags.length === 0 : activeTags.includes(tag.name);
                  
                  const handleTagClick = () => {
                    if (isAllTag) {
                      setActiveTags([]);
                    } else {
                      if (activeTags.includes(tag.name)) {
                        setActiveTags(activeTags.filter(t => t !== tag.name));
                      } else {
                        setActiveTags([...activeTags, tag.name]);
                      }
                    }
                  };

                  return (
                    <div
                      className={`booking-hub-tag ${isActive ? 'active' : ''}`}
                      key={tag.gid || index}
                      onClick={handleTagClick}
                      style={isActive ? { backgroundColor: hubDetails.colorCode || '#C9822A', borderColor: hubDetails.colorCode || '#C9822A', color: '#fff' } : {}}
                    >
                      {tag.name}
                    </div>
                  );
                })}
                {(activeTags.length > 0 || searchQuery) && (
                  <div
                    className="booking-hub-tag clear-all-btn"
                    onClick={() => {
                      setActiveTags([]);
                      setSearchQuery('');
                    }}
                    style={{
                      backgroundColor: '#fee2e2',
                      borderColor: '#fca5a5',
                      color: '#dc2626',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      padding: '4px 10px',
                      borderRadius: '9999px',
                      border: '1px solid',
                      fontSize: '11px',
                      marginLeft: '8px'
                    }}
                  >
                    Clear All
                  </div>
                )}
              </div>

              <button 
                onClick={() => scrollTags('right')}
                className="booking-hub-tags-scroll-btn booking-hub-tags-scroll-btn-right"
              >
                <RightOutlined style={{ fontSize: '10px' }} />
              </button>
            </div>
          )}
        </div>
      </div>

      {filteredLocations.length > 0 ? (
        <div className="booking-hub-grid">
          {filteredLocations.map((loc, index) => (
            <LocationCard key={loc.gid || index} loc={loc} gid={gid} />
          ))}
        </div>
      ) : (
        <div className="booking-hub-no-locations" style={{ textAlign: 'center', padding: '60px 20px', color: '#888', fontSize: '18px', fontWeight: '500' }}>
          No location found
        </div>
      )}
    </div>
  );
};

export default BookingHub;
